import {RectAreaLightTexturesLib, Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"

// Linearly Transformed Cosines (Heitz et al. 2016) for a sphere light, modeled as a disk facing the shading
// point. Reimplemented here because three only exposes these helpers for rectangular area lights; reuses
// three's rect-area-light BRDF lookup tables.

let ltcLib: ReturnType<typeof RectAreaLightTexturesLib.init> | null = null

export const getLtcTextures = (builder: THREE.NodeBuilder) => {
    if (!ltcLib) ltcLib = RectAreaLightTexturesLib.init()
    const float = (builder as unknown as {isAvailable(name: string): boolean}).isAvailable("float32Filterable")
    const ltc1 = TSL.texture(float ? ltcLib.LTC_FLOAT_1 : ltcLib.LTC_HALF_1)
    const ltc2 = TSL.texture(float ? ltcLib.LTC_FLOAT_2 : ltcLib.LTC_HALF_2)
    return {ltc1, ltc2}
}

// LUT uv: parameterized by roughness and sqrt(1 - N·V).
export const ltcUv = (n: THREE.Node<"vec3">, v: THREE.Node<"vec3">, roughness: THREE.Node<"float">) => {
    const LUT_SIZE = 64.0
    const LUT_SCALE = (LUT_SIZE - 1.0) / LUT_SIZE
    const LUT_BIAS = 0.5 / LUT_SIZE
    const dotNV = n.dot(v).clamp()
    return TSL.vec2(roughness, dotNV.oneMinus().sqrt()).mul(LUT_SCALE).add(LUT_BIAS)
}

// Rational approximation of theta/sin(theta)/2π × cross, summed over the polygon's edges.
const ltcEdgeVectorFormFactor = (v1: THREE.Node<"vec3">, v2: THREE.Node<"vec3">) => {
    const x = v1.dot(v2)
    const y = x.abs()
    const a = y.mul(0.0145206).add(0.4965155).mul(y).add(0.8543985)
    const b = y.add(4.1616724).mul(y).add(3.417594)
    const v = a.div(b)
    const thetaSinTheta = x.greaterThan(0.0).select(v, x.mul(x).oneMinus().max(1e-7).inverseSqrt().mul(0.5).sub(v))
    return v1.cross(v2).mul(thetaSinTheta)
}

const ltcClippedSphereFormFactor = (f: THREE.Node<"vec3">) => {
    const l = f.length()
    return l.mul(l).add(f.z).div(l.add(1.0)).max(0)
}

// Form factor of a disk (tessellated to a `segments`-gon) under the LTC matrix `mInv`; pass identity for the
// diffuse (cosine) lobe. center/axisU/axisV describe the disk in view space (|axisU| = |axisV| = radius).
export const ltcEvaluateDisk = (
    mInv: THREE.Node<"mat3">,
    center: THREE.Node<"vec3">,
    axisU: THREE.Node<"vec3">,
    axisV: THREE.Node<"vec3">,
    segments: number,
) => {
    const n = TSL.normalView
    const v = TSL.positionViewDirection
    const p = TSL.positionView
    const t1 = v.sub(n.mul(v.dot(n))).normalize()
    const t2 = n.cross(t1).negate()
    const mat = mInv.mul(TSL.mat3(t1, t2, n).transpose())

    // an inscribed n-gon covers only n·sin(2π/n)/2π of the disk's area (0.90 for n=8); inflate the
    // vertices to restore the disk's area, otherwise the light loses that fraction of its energy
    const areaCompensation = 1 / Math.sqrt((segments * Math.sin((2 * Math.PI) / segments)) / (2 * Math.PI))

    const coords: THREE.Node<"vec3">[] = []
    for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2
        const vert = center.add(axisU.mul(Math.cos(angle) * areaCompensation)).add(axisV.mul(Math.sin(angle) * areaCompensation))
        // safe normalize: a vertex coinciding with P (sphere crossing the surface) would give 0/0 → NaN
        const dir = mat.mul(vert.sub(p))
        coords.push(dir.div(dir.length().max(1e-4)).toVar())
    }

    const vectorFormFactor = TSL.vec3(0).toVar()
    for (let i = 0; i < segments; i++) {
        vectorFormFactor.addAssign(ltcEdgeVectorFormFactor(coords[i], coords[(i + 1) % segments]))
    }

    return ltcClippedSphereFormFactor(vectorFormFactor)
}
